import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle2, XCircle, Clock, DollarSign, Search } from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../../Toast';

export const WithdrawalsList: React.FC = () => {
    const [withdrawals, setWithdrawals] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredWithdrawals = withdrawals.filter(wd => {
        if (!searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase();
        return (wd.user_name || '').toLowerCase().includes(q) ||
            (wd.user_phone || '').toLowerCase().includes(q) ||
            (wd.user_email || '').toLowerCase().includes(q);
    });

    useEffect(() => {
        loadWithdrawals();
    }, []);

    const loadWithdrawals = async () => {
        try {
            setLoading(true);
            const res = await api.get('/banana/withdrawals');
            setWithdrawals(res.data.withdrawals);
        } catch (error) {
            console.error('Erro ao carregar saques:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id: string, action: 'approve' | 'reject' | 'complete') => {
        try {
            if (action === 'complete') {
                await api.post(`/banana/withdrawals/${id}/complete`);
                setToast({ msg: 'Saque marcado como concluído.', type: 'success' });
            } else {
                await api.post(`/banana/withdrawals/${id}/${action}`, action === 'reject' ? { reason: 'Solicitação não atende aos requisitos.' } : {});
                setToast({ msg: `Saque ${action === 'approve' ? 'aprovado' : 'rejeitado'} com sucesso!`, type: 'success' });
            }
            loadWithdrawals();
        } catch (error) {
            setToast({ msg: 'Erro ao processar ação.', type: 'error' });
        }
    };

    return (
        <div className="p-6">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <CreditCard className="text-orange-500" /> Saques Pendentes
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nome, telefone ou email..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-white text-sm focus:border-orange-500/50 outline-none"
                        />
                    </div>
                    <button onClick={loadWithdrawals} className="bg-orange-600 hover:bg-orange-500 text-white rounded-xl px-4 py-2 text-xs font-bold transition-all">
                        Atualizar
                    </button>
                </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full text-left">
                    <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5">
                        <tr>
                            <th className="p-5 font-normal uppercase tracking-wider">Usuário</th>
                            <th className="p-5 font-normal uppercase tracking-wider">Valor Solicitado</th>
                            <th className="p-5 font-normal uppercase tracking-wider">Status</th>
                            <th className="p-5 font-normal uppercase tracking-wider">Data</th>
                            <th className="p-5 font-normal uppercase tracking-wider text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? (
                            <tr><td colSpan={5} className="p-20 text-center text-gray-500">Carregando solicitações...</td></tr>
                        ) : filteredWithdrawals.length === 0 ? (
                            <tr><td colSpan={5} className="p-20 text-center text-gray-500">{withdrawals.length === 0 ? 'Nenhum saque encontrado.' : `Nenhum resultado para "${searchTerm}".`}</td></tr>
                        ) : filteredWithdrawals.map(wd => (
                            <tr key={wd.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                                <td className="p-5">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-white font-medium">{wd.user_name || 'Desconhecido'}</span>
                                            <span className="text-xs text-gray-500">({wd.user_phone})</span>
                                        </div>
                                        <small className="text-gray-500 text-[10px] font-mono">{wd.user_email || 'Sem Email'}</small>
                                        <small className="text-gray-600 text-[9px] font-mono mt-0.5">ID: {wd.id}</small>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center gap-1 text-green-400 font-bold">
                                        <DollarSign size={14} />
                                        <span>R$ {parseFloat(wd.requested_amount).toFixed(2)}</span>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className={`flex items-center gap-2 text-[10px] font-bold uppercase ${wd.status === 'completed' ? 'text-green-400' :
                                        wd.status === 'processing' ? 'text-blue-400' :
                                            wd.status === 'pending' ? 'text-orange-400' :
                                                'text-red-400'
                                        }`}>
                                        {wd.status === 'pending' && <Clock size={12} className="animate-pulse" />}
                                        {wd.status === 'processing' && <Clock size={12} />}
                                        {wd.status === 'completed' && <CheckCircle2 size={12} />}
                                        {wd.status === 'rejected' && <XCircle size={12} />}
                                        {wd.status === 'processing' ? 'Processando' : wd.status}
                                    </div>
                                </td>
                                <td className="p-5 text-gray-500">
                                    {new Date(wd.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                </td>
                                <td className="p-5 text-right">
                                    {wd.status === 'pending' ? (
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleAction(wd.id, 'approve')}
                                                className="bg-green-500/10 hover:bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all"
                                            >
                                                Aprovar
                                            </button>
                                            <button
                                                onClick={() => handleAction(wd.id, 'reject')}
                                                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all"
                                            >
                                                Rejeitar
                                            </button>
                                        </div>
                                    ) : wd.status === 'processing' ? (
                                        <button
                                            onClick={() => handleAction(wd.id, 'complete')}
                                            className="bg-green-500/10 hover:bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all"
                                            title="Valor já caiu na conta? Marque como concluído."
                                        >
                                            Marcar concluído
                                        </button>
                                    ) : (
                                        <span className="text-gray-600 text-[10px] font-bold uppercase tracking-widest">Finalizado</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
